import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import {
	GROUP_LABELS,
	type StraightenerParamGroup,
} from "../../straightener-common/parameterCatalog";
import type {
	StraightenerTrendData,
	StraightenerTrendRow,
} from "../models/StraightenerTrend";

interface SpRow {
	ParameterCode: string;
	DisplayName: string | null;
	GroupCode: StraightenerParamGroup | null;
	RollNumber: number;
	Recipe: number | null;
	Current: number | null;
	Min: number | null;
	Max: number | null;
	TextValue: string | null;
}

interface SpBanner {
	RecipeId: number | null;
	RecipeName: string | null;
	IsPlcCurrent: boolean | null;
	ProductSizeId: number | null;
	Version: number | null;
	Crew: string | null;
	ProductName: string | null;
	ProductTypeName: string | null;
	PlcSetupTag: string | null;
}

// Reads MILL.GetStraightenerTrendDisplayDataWeb: live Current per tag (already
// scaled and bit-decoded in the SP) + Recipe/Min/Max when a recipe is selected.
// Display name and group come from the SP too — the DB is the catalog.
export const getStraightenerTrend = async (
	recipeId: number | null,
): Promise<StraightenerTrendData> => {
	const api = new MESApiService();
	const params: spParameter[] = [{ name: "RecipeId", value: recipeId }];
	const resp = await api.callV2(
		"[MILL].[GetStraightenerTrendDisplayDataWeb]",
		params,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching straightener trend");
	}

	const spRows = get(resp, "data.tables[0].rows", []) as SpRow[];
	const banner = get(resp, "data.tables[1].rows[0]", null) as SpBanner | null;

	const rows: StraightenerTrendRow[] = [];
	for (const r of spRows) {
		rows.push({
			parameterCode: r.ParameterCode,
			displayName: r.DisplayName ?? r.ParameterCode,
			group: r.GroupCode ? (GROUP_LABELS[r.GroupCode] ?? "") : "",
			rollNumber: r.RollNumber ?? 0,
			recipe: r.Recipe,
			current: r.Current,
			min: r.Min,
			max: r.Max,
		});
	}

	return {
		banner: banner
			? {
					recipeId: banner.RecipeId ?? null,
					productSizeId: banner.ProductSizeId ?? null,
					name: banner.RecipeName ?? null,
					isPlcCurrent: !!banner.IsPlcCurrent,
					productName: banner.ProductName ?? "",
					productTypeName: banner.ProductTypeName ?? "",
					plcSetupTag: banner.PlcSetupTag ?? null,
					version: banner.Version,
					crew: banner.Crew,
				}
			: null,
		rows,
	};
};

// Enqueues one IH.WriteCommands row per sendToPlc recipe value (register =
// value × scale) plus the transfer trigger. Fire-and-forget: the queue has no
// synchronous ack, so success is confirmed by readback (see useSendToPlc), not
// by this call's return. Does not set IsPlcCurrent.
export const sendStraightenerRecipeToPlc = async (
	recipeId: number,
): Promise<void> => {
	const api = new MESApiService();
	const resp = await api.callV2("[MILL].[SendStraightenerRecipeToPlc]", [
		{ name: "RecipeId", value: recipeId },
	]);
	if (!resp.ok) {
		throw new Error(resp.message || "Error sending recipe to PLC");
	}
};

// Opens a straightener campaign for the product the operator picked, closing the
// open one. The Trend Display banner reads its product from that campaign.
export const startStraightenerCampaign = async (args: {
	productSizeId: number;
	scheduledBarsCount: number | null;
}): Promise<void> => {
	const api = new MESApiService();
	const resp = await api.callV2("[MILL].[StartStraightenerCampaign]", [
		{ name: "ProductSizeId", value: args.productSizeId },
		{ name: "ScheduledBarsCount", value: args.scheduledBarsCount },
	]);
	if (!resp.ok) {
		throw new Error(resp.message || "Error changing the straightener product");
	}
};
